import {
  formatDateForThreadList,
  getThreadPagePath,
  getThreadParticipantsPath,
} from "@/components/threads/util.ts";
import { Input } from "@/components/ui/input.tsx";
import { cn, isCoarsePointer } from "@/lib/utils.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import { useCallback, useState } from "react";
import { Link, useLocation } from "wouter";
import { StripMarkdown } from "../markdown/StripMarkdown.tsx";
import { THREAD_FLASH_ANIMATION, useThreadFlash } from "./threadFlash.ts";
import { CardFacepile } from "./CardFacepile.tsx";
import {
  THREAD_ACTION_REVEAL,
  ThreadPrimaryAction,
  ThreadRenameProvider,
  ThreadRowActionsMenu,
} from "./ThreadItemActions.tsx";
import { inlineRenameFieldClass, useInlineRename } from "./useInlineRename.ts";
import {
  compactRowClass,
  ExternalChannelBadge,
  InvitationConflictBadge,
  RoutineTriggeredBadge,
  threadAwaitsUser,
  ThreadStatusBadge,
} from "./shared.tsx";

type ThreadRowProps = {
  thread: GetThreadResp;
  /**
   * When provided, the row renders as a clickable surface that calls
   * `onSelect(thread.id)` instead of navigating to `/threads/:id`. Used by
   * sidebars that want to swap to a thread-detail view in place rather than
   * leaving the current page.
   */
  onSelect?: (threadId: number) => void;
  /**
   * This row's thread is the one showing on the canvas. Holds the hover pill
   * lit, the same treatment the nav list gives its active view — one list, one
   * meaning for "this is what you are looking at".
   */
  isOpen?: boolean;
};

/**
 * Renders a single thread as a row in a list.
 */
export const ThreadRow = ({ thread, onSelect, isOpen }: ThreadRowProps) => {
  const [, setLocation] = useLocation();
  const participants = thread.card?.participants ?? [];
  const additional = thread.card?.additionalParticipantCount ?? 0;
  // The whole row is already a `<Link>`, so the facepile navigates imperatively
  // rather than nesting an anchor. The nav boundary intercepts this the same
  // way it would a link, and picks the canvas, the sidebar, or the page.
  const openParticipants = useCallback(() => {
    setLocation(getThreadParticipantsPath(thread.id));
  }, [setLocation, thread.id]);

  const { flash, clearFlash } = useThreadFlash(thread.id);
  // Threads waiting on the user read bold; everything else regular. Read from
  // the same table that picks the row's mark, so the weight and the dot cannot
  // disagree about which rows those are.
  const needsAttention = threadAwaitsUser(thread);
  const coarsePointer = isCoarsePointer();

  const [isRenaming, setIsRenaming] = useState(false);
  const stopRenaming = useCallback(() => setIsRenaming(false), []);
  const startRenaming = useCallback(() => setIsRenaming(true), []);
  const { inputProps } = useInlineRename({
    threadId: thread.id,
    title: thread.title,
    isEditing: isRenaming,
    onDone: stopRenaming,
  });

  const shortDate = thread.sortDate ? formatDateForThreadList(thread.sortDate) : "";
  // Named once: the pointer and touch layouts place the same two controls
  // differently, and duplicating them inline is how the two drift apart.
  const rowActions = (
    <>
      <ThreadPrimaryAction thread={thread} surface="row" />
      <ThreadRowActionsMenu thread={thread} surface="row" />
    </>
  );

  const rowContent = (
    <div
      className={cn(
        // Mobile keeps the prototype's compact cards — 44px white rounded-xl
        // rows; from md up they flatten against the panel (no card backing,
        // border, or shadow) and a snug hover background is the affordance.
        //
        // 28px from md, down from 36. With the 4px gap the list gives it, the
        // pitch is 32px and no two hover surfaces ever touch. The 20px line of
        // text inside leaves 4px above and below it — the same 4px that
        // separates one card from the next, so the rhythm is even in both
        // directions.
        // A transparent border, not extra padding: the nav links above this list
        // carry a 1px border, so their icons sit 1px further right than the
        // same padding alone would put them. Matching the box model keeps the
        // status dot on their centerline and survives a padding change.
        "group/thread",
        compactRowClass,
        "md:h-7 md:rounded-lg md:bg-transparent md:px-2",
        "hover:bg-popover",
        // A rename holds the pill lit for as long as it lasts, and so does the
        // thread the canvas is showing. From `md` up the row is transparent
        // until hovered, so without this the surface the user is typing on
        // drops away the moment the pointer leaves to do anything else — and
        // the field they are editing has nothing under it.
        (isRenaming || isOpen) && "md:bg-popover",
        flash && "animate-thread-flash",
      )}
      onAnimationEnd={
        flash
          ? (e) => {
              if (e.animationName === THREAD_FLASH_ANIMATION) clearFlash();
            }
          : undefined
      }
    >
      {/* Column 1: the marks. The status dot sits centered in a fixed slot
          rather than at its own width, so the dots line up down the list and on
          the centerline of the sidebar nav icons in the column above. */}
      <div className="flex flex-shrink-0 items-center gap-1">
        <span className="flex w-3.5 shrink-0 justify-center">
          <ThreadStatusBadge thread={thread} compact />
        </span>
        <ExternalChannelBadge thread={thread} compact />
        <InvitationConflictBadge thread={thread} compact />
        <RoutineTriggeredBadge thread={thread} compact />
      </div>

      {/* Column 2: Title and Content (truncatable). `pl-2` is the gap from the
            mark column, which has to come from here: the marks carry no ground
            of their own, so nothing on their side sets it. `pr-1` keeps the
            trailing edge tighter, where a date sits against the row's padding. */}
      <div className="flex-1 min-w-0 text-sm truncate pl-2 pr-1">
        {isRenaming ? (
          // The preview copy steps aside for the whole edit: the row gives the
          // title about a third of its width when a summary is sharing the line,
          // which is not enough of a name to edit inside of.
          <Input
            {...inputProps}
            className={cn(
              inlineRenameFieldClass,
              "w-full",
              needsAttention ? "font-semibold" : "font-normal",
            )}
          />
        ) : (
          <span className={needsAttention ? "font-semibold" : "font-normal"}>
            {thread.title || "New thread"}
          </span>
        )}
        {!isRenaming && (thread.card?.body || thread.activeMessage?.content) && (
          <>
            &ensp;
            <span className="hidden @md:inline">-&ensp;</span>
          </>
        )}
        {/* Prefer the server-emitted card copy; fall back to the raw
              message content (Gmail-style) on paths that don't populate
              `card`. */}
        {!isRenaming && (
          <StripMarkdown>
            {thread.card?.body ?? thread.activeMessage?.content?.trim() ?? ""}
          </StripMarkdown>
        )}
      </div>

      {/* Column 4: the date at rest, and everything the row can be acted on with
            in its place while hovered — the faces included. At rest the row is a
            title, a line of the body, and a date, which is what a list is read
            for; the faces are a control, not a label, and they earn their space
            at the moment the pointer says the user is working on this thread
            rather than scanning past it.

            The cluster overlays rather than reserving its own column: it is
            wider than a short date, so reserving would cost the title ~50
            characters on every row for something shown on one. It carries the
            row's own hover background to cover the tail of the title it
            overhangs — without that, title text reads through the gaps between
            the glyphs.

            The whole column stands down for a rename. None of it applies to what
            the user is doing — the actions act on a thread that is mid-edit, and
            the date and faces are only reference — and standing down hands the
            field the row's full width, which a long name needs more than a
            36px row can spare. */}
      {!isRenaming && (
        <div className="relative flex items-center flex-shrink-0 pl-1">
          {/* Gone below `md`. A phone-width row is a badge, a title and two
              controls, and the title is what the row is for — the date was
              taking characters off it to say something the thread's own screen
              says better. */}
          <span
            className={cn(
              "hidden text-xs text-muted-foreground md:inline",
              !coarsePointer && "group-hover/thread:invisible",
            )}
          >
            {shortDate}
          </span>
          <div
            className={cn(
              // `gap-0.5` throughout: the faces and the two buttons read as one
              // cluster only if the seam between them matches the seam between
              // the buttons.
              "flex items-center gap-0.5",
              // On a touch screen the controls have to earn their own width:
              // there is no hover to trade the date for, so they sit beside it.
              coarsePointer
                ? "ml-1"
                : cn("absolute inset-y-0 right-0 pl-3 bg-popover", THREAD_ACTION_REVEAL),
            )}
          >
            {(participants.length > 0 || additional > 0) && (
              <CardFacepile
                participants={participants}
                additional={additional}
                onClick={openParticipants}
                // Below `md` the faces move into the overflow menu's Contacts
                // row instead, where they are still glanceable but cost the
                // title none of its width. Card view always shows the facepile.
                className="hidden md:flex"
              />
            )}
            {rowActions}
          </div>
        </div>
      )}
    </div>
  );

  // While the title is being edited the row is not a link: clicking into the
  // field, or dragging to select a word in it, would otherwise leave for the
  // thread mid-rename. Checked ahead of the modifier keys so cmd-click does not
  // open a second tab on a thread the user is in the middle of naming.
  //
  // Otherwise, when `onSelect` is supplied, intercept the click and call the
  // handler instead of navigating. The <Link> remains so right-click /
  // cmd-click "open in new tab" and keyboard semantics still work — only the
  // default left-click navigation is hijacked.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isRenaming) {
      e.preventDefault();
      return;
    }
    if (!onSelect) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onSelect(thread.id);
  };

  return (
    <ThreadRenameProvider value={startRenaming}>
      <Link
        href={getThreadPagePath(thread.id)}
        className="no-underline"
        onClick={handleClick}
        // An `<a href>` is draggable by default, and that wins over selecting
        // text inside it: press-and-drag across the title field started dragging
        // the *link* — ghost image, URL and all — instead of highlighting a
        // word, and dropping it back on the field pasted the thread's URL into
        // the name. Only while renaming; dragging a thread link out to a
        // bookmark bar or another window is worth keeping the rest of the time.
        draggable={isRenaming ? false : undefined}
      >
        {rowContent}
      </Link>
    </ThreadRenameProvider>
  );
};
